<template>
  <b-form @submit.prevent="submit">
    <b-form-group label="เปิดใช้งานระบบระดับลูกค้า" label-cols-sm="4" label-size="sm" class="mb-2">
      <b-form-checkbox v-model="form.isEnabled" switch :disabled="isLoading">
        {{ form.isEnabled ? "เปิดใช้งาน" : "ปิดใช้งาน" }}
      </b-form-checkbox>
    </b-form-group>

    <b-form-group
      label="ช่วงเวลาคำนวณยอดสะสม (เดือน) *"
      label-cols-sm="4"
      label-size="sm"
      class="mb-2"
      :state="windowMonthsErr ? false : null"
      :invalid-feedback="windowMonthsErr"
    >
      <b-form-input
        v-model.number="form.windowMonths"
        type="number"
        min="1"
        size="sm"
        :disabled="isLoading"
        :state="windowMonthsErr ? false : null"
      />
      <small class="text-muted">ยอดใช้จ่ายย้อนหลังกี่เดือนที่นำมานับเพื่อจัดระดับ</small>
    </b-form-group>

    <b-form-group
      label="จำนวนวันผ่อนผันก่อนลดระดับ *"
      label-cols-sm="4"
      label-size="sm"
      class="mb-2"
      :state="graceDaysErr ? false : null"
      :invalid-feedback="graceDaysErr"
    >
      <b-form-input
        v-model.number="form.graceDays"
        type="number"
        min="0"
        size="sm"
        :disabled="isLoading"
        :state="graceDaysErr ? false : null"
      />
      <small class="text-muted">ใส่ 0 หากต้องการลดระดับทันทีเมื่อยอดสะสมต่ำกว่าเกณฑ์ (ไม่มีช่วงผ่อนผัน)</small>
    </b-form-group>

    <b-alert :show="!!errorMessage" variant="danger">{{ errorMessage }}</b-alert>
    <b-alert :show="!!savedMessage" variant="success" dismissible @dismissed="savedMessage = ''">
      {{ savedMessage }}
    </b-alert>

    <div class="d-flex justify-content-end">
      <b-button type="submit" variant="primary" size="sm" :disabled="!canSubmit || isSaving">
        บันทึกการตั้งค่า
      </b-button>
    </div>
  </b-form>
</template>

<script>
import { mapActions } from "vuex";
import { windowMonthsError as windowMonthsErrorOf, graceDaysError as graceDaysErrorOf } from "@/utils/tierValidation";
import { tierErrorMessage } from "@/utils/tierErrors";

export default {
  name: "TierConfigForm",
  data() {
    return {
      isLoading: false,
      isSaving: false,
      errorMessage: "",
      savedMessage: "",
      form: {
        isEnabled: false,
        windowMonths: 12,
        graceDays: 90,
      },
    };
  },
  computed: {
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    // v-model.number leaves the field as the empty string (not 0) once the
    // user clears it, so these validators only see a real number when the
    // input actually holds one — the graceDays: 0 case still passes because
    // graceDaysError checks `typeof value === "number"`, not truthiness.
    windowMonthsErr() {
      return windowMonthsErrorOf(this.form.windowMonths);
    },
    graceDaysErr() {
      return graceDaysErrorOf(this.form.graceDays);
    },
    canSubmit() {
      return !this.isLoading && !this.isSaving && !this.windowMonthsErr && !this.graceDaysErr;
    },
  },
  methods: {
    ...mapActions("moduleTier", ["getConfig", "updateConfig"]),

    async load() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        const config = await this.getConfig({ clinicUrl: this.clinicUrl });
        this.form.isEnabled = !!config.isEnabled;
        this.form.windowMonths = config.windowMonths;
        this.form.graceDays = config.graceDays;
      } catch (error) {
        this.errorMessage = tierErrorMessage(error) || "โหลดการตั้งค่าระบบระดับลูกค้าไม่สำเร็จ";
      } finally {
        this.isLoading = false;
      }
    },

    async submit() {
      if (!this.canSubmit) return;
      this.isSaving = true;
      this.errorMessage = "";
      this.savedMessage = "";
      try {
        await this.updateConfig({
          clinicUrl: this.clinicUrl,
          data: {
            isEnabled: !!this.form.isEnabled,
            windowMonths: Number(this.form.windowMonths),
            graceDays: Number(this.form.graceDays),
          },
        });
        this.savedMessage = "บันทึกการตั้งค่าเรียบร้อยแล้ว";
        this.$emit("saved");
      } catch (error) {
        // Let the backend's own validation message win — it is authoritative
        // (see updateConfig.js's isEnabled/windowMonths/graceDays checks).
        this.errorMessage = tierErrorMessage(error) || "บันทึกการตั้งค่าไม่สำเร็จ";
      } finally {
        this.isSaving = false;
      }
    },
  },
  created() {
    this.load();
  },
};
</script>
